import { AlertCircle } from 'lucide-react';
import { hasStaffRole } from '@hcc/types';

/**
 * Per-user mobilisation access editor + read view, used in the admin
 * Users table. Super_admin has implicit access to every mobilisation,
 * so the table shows "All" and the editor is a no-op note. Inductee-only
 * users never carry staff access, shown as a dash.
 */

export interface MobLite {
  id: string;
  name: string;
  date: string;
  status: 'planning' | 'active' | 'closed';
}

function isSuper(roles: readonly string[]): boolean {
  return roles.includes('super_admin');
}

function isStaff(roles: readonly string[]): boolean {
  return hasStaffRole(roles);
}

export function UserMobilisationsRead({
  roles,
  selected,
  all,
}: {
  roles: string[];
  selected: string[];
  all: MobLite[];
}) {
  if (isSuper(roles)) {
    return <span className="text-xs text-fg-muted">All</span>;
  }
  if (!isStaff(roles)) {
    return <span className="text-xs text-fg-dim">—</span>;
  }
  if (selected.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft/15 px-2 py-0.5 text-[11px] font-medium text-warning-soft-fg">
        <AlertCircle className="h-3 w-3" />
        none
      </span>
    );
  }
  const byId = new Map(all.map((m) => [m.id, m]));
  return (
    <div className="flex flex-wrap gap-1">
      {selected.map((id) => (
        <span
          key={id}
          className="rounded-md bg-surface-2 px-2 py-0.5 text-xs font-medium text-fg-muted"
        >
          {byId.get(id)?.name ?? id.slice(0, 8)}
        </span>
      ))}
    </div>
  );
}

export function UserMobilisationsEdit({
  roles,
  value,
  onChange,
  all,
}: {
  roles: string[];
  value: string[];
  onChange: (next: string[]) => void;
  all: MobLite[];
}) {
  if (isSuper(roles)) {
    return (
      <span className="text-xs text-fg-muted">
        All mobilisations (super admin)
      </span>
    );
  }
  if (all.length === 0) {
    return <span className="text-xs text-fg-dim">No mobilisations yet</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {all.map((m) => {
        const checked = value.includes(m.id);
        return (
          <label
            key={m.id}
            className="flex items-center gap-2 text-xs text-fg-muted"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? [...value, m.id]
                    : value.filter((x) => x !== m.id),
                )
              }
              className="h-3.5 w-3.5 rounded border-line-strong/30 text-accent focus-visible:ring-2 focus-visible:ring-ring/30"
            />
            <span>
              {m.name}{' '}
              <span className="text-fg-dim">({m.status})</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
